/**
 * Enterprise Standard Order Calculator
 * 
 * REGRA CRÍTICA: discount_value é SEMPRE por unidade
 * Total do desconto = discount_value × quantity
 * 
 * Este utilitário centraliza todos os cálculos de pedidos/vendas/orçamentos
 * para garantir consistência em todo o sistema.
 */

export interface OrderItemInput {
  quantity: number;
  unit_price: number;
  discount_percentage?: number;
  discount_value?: number; // POR UNIDADE
  discount_type?: 'percentage' | 'fixed' | null; // Tipo de desconto para priorização
}

export interface OrderItemCalculation {
  subtotal: number;        // quantity × unit_price
  discountAmount: number;  // desconto TOTAL (por unidade × quantity)
  total: number;           // subtotal - discountAmount
  discountPerUnit: number; // desconto por unidade (para exibição)
}

export interface OrderSummary {
  grossSubtotal: number;   // soma dos subtotais dos itens (sem descontos)
  totalDiscount: number;   // soma de todos os descontos de itens
  netSubtotal: number;     // grossSubtotal - totalDiscount
}

/**
 * Arredondamento monetário consistente
 * Evita problemas de ponto flutuante (ex: 0.1 + 0.2 = 0.30000000000000004)
 */
export const roundMoney = (value: number): number => 
  Math.round(value * 100) / 100;

/**
 * Calcula os totais de um único item
 * 
 * @param item - Item com quantity, unit_price e opcionalmente discount_percentage ou discount_value
 * @returns Cálculos do item incluindo subtotal, desconto total e total líquido
 * 
 * IMPORTANTE: 
 * - discount_value é tratado como desconto POR UNIDADE
 * - O desconto total é calculado como discount_value × quantity
 * - discount_type define a prioridade do cálculo (evita inferência incorreta)
 */
export function calculateItemTotals(item: OrderItemInput): OrderItemCalculation {
  const quantity = item.quantity || 1;
  const unitPrice = item.unit_price || 0;
  const discountPercentage = item.discount_percentage || 0;
  const discountValuePerUnit = item.discount_value || 0;
  const discountType = item.discount_type;
  
  const subtotal = roundMoney(quantity * unitPrice);
  
  let discountAmount = 0;
  let discountPerUnit = 0;
  
  // CRITICAL: Usar discount_type para determinar qual campo é a "fonte da verdade"
  // Isso elimina erros de arredondamento ao editar vendas
  if (discountType === 'fixed') {
    // Usuário escolheu desconto em R$ - usar discount_value diretamente
    discountPerUnit = discountValuePerUnit;
    discountAmount = roundMoney(discountValuePerUnit * quantity);
  } else if (discountType === 'percentage') {
    // Usuário escolheu desconto em % - calcular a partir do percentual
    discountPerUnit = roundMoney((unitPrice * discountPercentage) / 100);
    discountAmount = roundMoney((subtotal * discountPercentage) / 100);
  } else {
    // LEGADO (discount_type = null): priorizar valor fixo
    // O percentual frequentemente tem dízimas (5.56% → 10.008)
    // enquanto o valor fixo é o que o usuário realmente digitou
    if (discountValuePerUnit > 0) {
      discountPerUnit = discountValuePerUnit;
      discountAmount = roundMoney(discountValuePerUnit * quantity);
    } else if (discountPercentage > 0) {
      discountPerUnit = roundMoney((unitPrice * discountPercentage) / 100);
      discountAmount = roundMoney((subtotal * discountPercentage) / 100);
    }
  }
  
  return {
    subtotal,
    discountAmount,
    total: roundMoney(subtotal - discountAmount),
    discountPerUnit,
  };
}

/**
 * Calcula o resumo de todos os itens de um pedido
 * 
 * @param items - Array de itens do pedido
 * @returns Resumo com subtotal bruto, desconto total e subtotal líquido
 * 
 * NOTA: Este cálculo NÃO inclui descontos globais do pedido nem frete.
 * Esses valores devem ser adicionados/subtraídos separadamente.
 */
export function calculateOrderSummary(items: OrderItemInput[]): OrderSummary {
  let grossSubtotal = 0;
  let totalDiscount = 0;
  let netSubtotal = 0;
  
  for (const item of items) {
    const calc = calculateItemTotals(item);
    grossSubtotal = roundMoney(grossSubtotal + calc.subtotal);
    totalDiscount = roundMoney(totalDiscount + calc.discountAmount);
    netSubtotal = roundMoney(netSubtotal + calc.total);
  }
  
  return { grossSubtotal, totalDiscount, netSubtotal };
}

/**
 * Calcula o desconto global (nível do pedido) baseado em percentual ou valor fixo
 * 
 * @param baseValue - Valor base para cálculo (geralmente netSubtotal dos itens)
 * @param discountPercentage - Percentual de desconto global (0-100)
 * @param discountValue - Valor fixo de desconto global
 * @returns Valor do desconto global a ser aplicado
 */
export type GlobalDiscountMode = 'percent' | 'fixed' | null;

/**
 * Desconto global (nível pedido).
 * - mode 'fixed': usa apenas discountValue (R$), ignora percentual.
 * - mode 'percent': usa apenas discountPercentage sobre baseValue (subtotal líquido dos itens).
 * - mode null (legado): se discountValue > 0 trata como fixo; senão aplica percentual.
 */
export function calculateGlobalDiscount(
  baseValue: number,
  discountPercentage: number = 0,
  discountValue: number = 0,
  mode: GlobalDiscountMode = null
): number {
  if (mode === 'fixed') {
    return discountValue > 0 ? roundMoney(discountValue) : 0;
  }
  if (mode === 'percent') {
    return discountPercentage > 0 ? roundMoney((baseValue * discountPercentage) / 100) : 0;
  }
  if (discountValue > 0) {
    return roundMoney(discountValue);
  }
  if (discountPercentage > 0) {
    return roundMoney((baseValue * discountPercentage) / 100);
  }
  return 0;
}

/**
 * Calcula o total final do pedido
 * 
 * @param items - Array de itens do pedido
 * @param globalDiscountPercentage - Percentual de desconto adicional global
 * @param globalDiscountValue - Valor fixo de desconto adicional global
 * @param freightValue - Valor do frete
 * @returns Valor total final do pedido
 */
export function calculateOrderTotal(
  items: OrderItemInput[],
  globalDiscountPercentage: number = 0,
  globalDiscountValue: number = 0,
  freightValue: number = 0,
  globalDiscountMode: GlobalDiscountMode = null
): number {
  const summary = calculateOrderSummary(items);
  const globalDiscount = calculateGlobalDiscount(
    summary.netSubtotal,
    globalDiscountPercentage,
    globalDiscountValue,
    globalDiscountMode
  );

  return roundMoney(summary.netSubtotal - globalDiscount + roundMoney(freightValue));
}
